import { useCallback, useEffect, useMemo, useState } from 'react';
import { Camera, Edit, Plus, Search, Stethoscope, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createProfessional,
  listProfessionals,
  updateProfessional,
  updateProfessionalStatus,
  uploadProfessionalPhoto
} from '../api/professionals';
import { getErrorMessage } from '../api/response';
import { getBranches } from '../api/tenant';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { resizeImageFile } from '../utils/images';
import { activeStatus, getStatusConfig } from '../utils/status';

const emptyForm = {
  full_name: '',
  email: '',
  phone: '',
  role: 'veterinarian',
  specialty: '',
  branch_ids: [],
  bio: '',
  active: true,
  photo: null
};

const Veterinarios = () => {
  const [professionals, setProfessionals] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('veterinarian');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmStatus, setConfirmStatus] = useState(null);

  const branchById = useMemo(() => new Map(branches.map((branch) => [Number(branch.id), branch])), [branches]);
  const debouncedSearch = useDebouncedValue(search.trim());

  const loadProfessionals = useCallback(async () => {
    try {
      setLoading(true);
      const [professionalData, branchData] = await Promise.all([
        listProfessionals({
          search: debouncedSearch || undefined,
          role: role || undefined,
          include_inactive: includeInactive ? 1 : undefined
        }),
        getBranches()
      ]);

      setProfessionals(professionalData);
      setBranches(branchData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar los profesionales'));
    } finally {
      setLoading(false);
    }
  }, [includeInactive, role, debouncedSearch]);

  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (professional) => {
    setEditing(professional);
    setForm({
      full_name: professional.full_name || '',
      email: professional.email || '',
      phone: professional.phone || '',
      role: professional.role || 'veterinarian',
      specialty: professional.specialties?.length ? professional.specialties.join(', ') : professional.specialty || '',
      branch_ids: (professional.branch_ids?.length ? professional.branch_ids : professional.branch_id ? [professional.branch_id] : []).map(String),
      bio: professional.bio || '',
      active: Boolean(Number(professional.is_active)),
      photo: null
    });
    setModalOpen(true);
  };

  const toggleBranch = (branchId) => {
    const value = String(branchId);
    const exists = form.branch_ids.includes(value);
    setForm({
      ...form,
      branch_ids: exists
        ? form.branch_ids.filter((item) => item !== value)
        : [...form.branch_ids, value]
    });
  };

  const saveProfessional = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      const branchIds = form.branch_ids.map((item) => Number(item)).filter(Boolean);
      const payload = {
        full_name: form.full_name,
        email: form.email || null,
        phone: form.phone || null,
        role: form.role,
        specialty: form.specialty,
        specialties: form.specialty.split(',').map((item) => item.trim()).filter(Boolean),
        branch_id: branchIds[0] || null,
        branch_ids: branchIds,
        bio: form.bio || null,
        active: form.active
      };

      const saved = editing
        ? await updateProfessional(editing.id, payload)
        : await createProfessional(payload);

      if (form.photo) {
        const resized = await resizeImageFile(form.photo);
        await uploadProfessionalPhoto(saved.id, resized);
      }

      toast.success(editing ? 'Profesional actualizado' : 'Profesional creado');
      setModalOpen(false);
      loadProfessionals();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar el profesional'));
    } finally {
      setSaving(false);
    }
  };

  const confirmToggleStatus = async () => {
    if (!confirmStatus) return;

    try {
      setSaving(true);
      const nextActive = !Boolean(Number(confirmStatus.is_active));
      await updateProfessionalStatus(confirmStatus.id, nextActive);
      toast.success(nextActive ? 'Profesional activado' : 'Profesional desactivado');
      setConfirmStatus(null);
      loadProfessionals();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cambiar el estado'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'full_name',
      label: 'Profesional',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-md bg-blue-50 text-blue-700">
            {row.photo_url ? (
              <img src={row.photo_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <Stethoscope className="h-5 w-5" />
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{row.full_name}</p>
            <p className="text-xs text-gray-500">{row.email || 'Sin email'}</p>
          </div>
        </div>
      )
    },
    { key: 'role', label: 'Rol', render: (row) => row.role === 'veterinarian' ? 'Veterinario' : 'Asistente' },
    { key: 'specialty', label: 'Especialidades', render: (row) => row.specialties?.length ? row.specialties.join(', ') : row.specialty || '-' },
    {
      key: 'branch_ids',
      label: 'Sucursales',
      render: (row) => {
        const ids = row.branch_ids?.length ? row.branch_ids : row.branch_id ? [row.branch_id] : [];
        return ids.map((id) => branchById.get(Number(id))?.name || `#${id}`).join(', ') || '-';
      }
    },
    {
      key: 'is_active',
      label: 'Estado',
      render: (row) => {
        const status = getStatusConfig(activeStatus, Number(row.is_active));
        return <StatusBadge label={status.label} color={status.color} />;
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => openEdit(row)} className="rounded-md p-2 text-blue-700 hover:bg-blue-50" title="Editar">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={() => setConfirmStatus(row)} className="rounded-md p-2 text-gray-700 hover:bg-gray-100" title="Activar o desactivar">
            {Number(row.is_active) === 1 ? <ToggleRight className="h-4 w-4 text-green-700" /> : <ToggleLeft className="h-4 w-4 text-gray-500" />}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Veterinarios"
        description="Gestion de profesionales, especialidades, sucursales asociadas y foto de perfil."
        actions={<Button icon={Plus} onClick={openCreate}>Nuevo profesional</Button>}
      />

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <FormField label="Buscar">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input className={`${inputClassName} pl-9`} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nombre profesional" />
            </div>
          </FormField>
          <FormField label="Rol">
            <select className={inputClassName} value={role} onChange={(event) => setRole(event.target.value)}>
              <option value="">Todos</option>
              <option value="veterinarian">Veterinarios</option>
              <option value="assistant">Asistentes</option>
            </select>
          </FormField>
          <FormField label="Estado">
            <select className={inputClassName} value={includeInactive ? 'all' : 'active'} onChange={(event) => setIncludeInactive(event.target.value === 'all')}>
              <option value="all">Todos</option>
              <option value="active">Solo activos</option>
            </select>
          </FormField>
          <div className="flex items-end">
            <Button variant="secondary" onClick={loadProfessionals} className="w-full">Actualizar</Button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? <div className="p-5"><LoadingState rows={4} /></div> : (
          <Table columns={columns} data={professionals} emptyMessage="No hay profesionales para el filtro seleccionado" />
        )}
      </section>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar profesional' : 'Nuevo profesional'}
        onClose={() => setModalOpen(false)}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
            <Button type="submit" form="professional-form" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </div>
        }
      >
        <form id="professional-form" onSubmit={saveProfessional} className="grid gap-4 md:grid-cols-2">
          <FormField label="Nombre completo" required>
            <input className={inputClassName} value={form.full_name} onChange={(event) => setForm({ ...form, full_name: event.target.value })} required />
          </FormField>
          <FormField label="Rol" required>
            <select className={inputClassName} value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} required>
              <option value="veterinarian">Veterinario</option>
              <option value="assistant">Asistente</option>
            </select>
          </FormField>
          <FormField label="Email">
            <input type="email" className={inputClassName} value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
          </FormField>
          <FormField label="Telefono">
            <input className={inputClassName} value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
          </FormField>
          <FormField label="Especialidades" hint="Separar por coma. Ej: Cirugia, Dermatologia">
            <input className={inputClassName} value={form.specialty} onChange={(event) => setForm({ ...form, specialty: event.target.value })} />
          </FormField>
          <FormField label="Foto de perfil">
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
              <Camera className="h-4 w-4" />
              <span>{form.photo ? form.photo.name : 'Seleccionar imagen'}</span>
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(event) => setForm({ ...form, photo: event.target.files?.[0] || null })} />
            </label>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Sucursales asociadas">
              <div className="grid gap-2 rounded-md border border-gray-200 p-3 md:grid-cols-2">
                {branches.map((branch) => (
                  <label key={branch.id} className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={form.branch_ids.includes(String(branch.id))} onChange={() => toggleBranch(branch.id)} />
                    {branch.name}
                  </label>
                ))}
                {branches.length === 0 && <p className="text-sm text-gray-500">No hay sucursales configuradas.</p>}
              </div>
            </FormField>
          </div>
          <FormField label="Estado">
            <select className={inputClassName} value={String(form.active)} onChange={(event) => setForm({ ...form, active: event.target.value === 'true' })}>
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Bio / perfil profesional">
              <textarea rows={4} className={inputClassName} value={form.bio} onChange={(event) => setForm({ ...form, bio: event.target.value })} />
            </FormField>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmStatus)}
        title={Number(confirmStatus?.is_active) === 1 ? 'Desactivar profesional' : 'Activar profesional'}
        message={`Se cambiara el estado de ${confirmStatus?.full_name}.`}
        confirmLabel={Number(confirmStatus?.is_active) === 1 ? 'Desactivar' : 'Activar'}
        loading={saving}
        onCancel={() => setConfirmStatus(null)}
        onConfirm={confirmToggleStatus}
      />
    </div>
  );
};

export default Veterinarios;
